/**
 * Welle 9 (pre89c): Zielordner PRO Dokumententyp (Scan / signierter Vertrag / EC-Karte)
 * für die automatische Ablage und die Nachbearbeitung. File System Access API (Chromium):
 * der Berater wählt je Typ EINMAL einen Ordner (Browser-Sicherheit — ein fester Pfad per
 * Code ist nicht möglich), der Handle landet in IndexedDB, danach speichern Downloads ohne
 * Dialog direkt dort. Zusätzlich `saveWithDialog` für „Speichern unter …" (immer verfügbar).
 */

export type TargetKind = 'scan' | 'signed' | 'nfc';

const DB_NAME = 'bamoa-fs';
const STORE = 'handles';
const KEY = (k: TargetKind) => `dir:${k}`;

export function dirPickerSupported(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}
export function savePickerSupported(): boolean {
  return typeof window !== 'undefined' && 'showSaveFilePicker' in window;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGet<T>(key: string): Promise<T | null> {
  const db = await openDb();
  const out = await new Promise<T | null>((res, rej) => {
    const tx = db.transaction(STORE, 'readonly');
    const r = tx.objectStore(STORE).get(key);
    r.onsuccess = () => res((r.result as T) ?? null);
    r.onerror = () => rej(r.error);
  });
  db.close();
  return out;
}
async function idbPut(key: string, val: any): Promise<void> {
  const db = await openDb();
  await new Promise<void>((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(val, key);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
  db.close();
}
async function idbDel(key: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
  db.close();
}

/** Ordner für einen Typ wählen (Nutzer-Geste nötig). Gibt den Ordnernamen zurück oder null. */
export async function pickDir(kind: TargetKind): Promise<string | null> {
  if (!dirPickerSupported()) return null;
  try {
    const handle = await (window as any).showDirectoryPicker({ mode: 'readwrite', id: `bamoa-${kind}` });
    await idbPut(KEY(kind), handle);
    return handle.name ?? 'Ordner';
  } catch (e: any) {
    if (e?.name === 'AbortError') return null;
    console.warn('[fileTargets] pick fehlgeschlagen', e);
    return null;
  }
}
export async function getDirName(kind: TargetKind): Promise<string | null> {
  try { const h = await idbGet<any>(KEY(kind)); return h?.name ?? null; } catch { return null; }
}
export async function getAllDirNames(): Promise<Record<TargetKind, string | null>> {
  const [scan, signed, nfc] = await Promise.all([getDirName('scan'), getDirName('signed'), getDirName('nfc')]);
  return { scan, signed, nfc };
}
export async function clearDir(kind: TargetKind): Promise<void> {
  try { await idbDel(KEY(kind)); } catch { /* */ }
}

export type AccessState = 'none' | 'granted' | 'prompt' | 'denied';

/** Alle gemerkten Verzeichnis-Handles laden (für die spätere synchrone Berechtigungsanfrage). */
export async function getHandles(): Promise<Record<TargetKind, any | null>> {
  const [scan, signed, nfc] = await Promise.all([
    idbGet<any>(KEY('scan')), idbGet<any>(KEY('signed')), idbGet<any>(KEY('nfc')),
  ]);
  return { scan, signed, nfc };
}

async function queryAccess(handle: any): Promise<AccessState> {
  try { return (await handle.queryPermission({ mode: 'readwrite' })) as AccessState; } catch { return 'denied'; }
}

/** Berechtigungszustand je Typ (ohne Nutzer-Geste). */
export async function getAccessStates(handles?: Record<TargetKind, any | null>): Promise<Record<TargetKind, AccessState>> {
  const hs = handles ?? await getHandles();
  const out = { scan: 'none', signed: 'none', nfc: 'none' } as Record<TargetKind, AccessState>;
  await Promise.all((['scan', 'signed', 'nfc'] as TargetKind[]).map(async (k) => {
    out[k] = hs[k] ? await queryAccess(hs[k]) : 'none';
  }));
  return out;
}

/**
 * Berechtigung für ALLE gemerkten Ordner mit EINEM Klick neu anfragen. MUSS aus einem
 * Klick-Handler aufgerufen werden und die `handles` müssen bereits im Speicher liegen — die
 * `requestPermission`-Aufrufe werden synchron abgefeuert, damit die Nutzer-Aktivierung für
 * jeden Ordner erhalten bleibt (kein `await` davor).
 */
export function requestAccessAll(handles: Record<TargetKind, any | null>): Promise<void> {
  const proms: Promise<any>[] = [];
  for (const k of ['scan', 'signed', 'nfc'] as TargetKind[]) {
    const h = handles[k];
    if (h) proms.push(h.requestPermission({ mode: 'readwrite' }).catch(() => { /* */ }));
  }
  return Promise.all(proms).then(() => { /* */ });
}

/**
 * Berechtigung für EIN bereits geladenes Handle anfragen. MUSS aus einem Klick-Handler
 * aufgerufen werden und das Handle muss schon im Speicher liegen (kein `await` davor), damit
 * die Nutzer-Aktivierung für `requestPermission` erhalten bleibt (wichtig in Edge!).
 */
export function requestAccessFor(handle: any | null): Promise<boolean> {
  if (!handle) return Promise.resolve(false);
  try {
    return handle.requestPermission({ mode: 'readwrite' }).then((s: string) => s === 'granted').catch(() => false);
  } catch {
    return Promise.resolve(false);
  }
}

async function ensurePermission(handle: any, interactive: boolean): Promise<boolean> {
  const opts = { mode: 'readwrite' as const };
  try {
    if ((await handle.queryPermission(opts)) === 'granted') return true;
    if (interactive && (await handle.requestPermission(opts)) === 'granted') return true;
  } catch { /* */ }
  return false;
}

async function uniqueName(dir: any, name: string): Promise<string> {
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  let candidate = name;
  let i = 1;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      await dir.getFileHandle(candidate, { create: false });
      candidate = `${base} (${i})${ext}`;
      i++;
      if (i > 999) return candidate;
    } catch {
      return candidate;
    }
  }
}

export type SaveResult =
  | { ok: true; name: string }
  | { ok: false; reason: 'no-dir' | 'denied' | 'error' };

/**
 * Blob direkt in den Ordner des Typs schreiben (ohne Dialog).
 * interactive=false bei Auto-Ablage (kein Nutzer-Gesten-Kontext) → fragt KEINE Berechtigung an.
 */
export async function saveToDir(
  kind: TargetKind,
  name: string,
  blob: Blob,
  opts: { interactive?: boolean } = {},
): Promise<SaveResult> {
  const handle = await idbGet<any>(KEY(kind));
  if (!handle) return { ok: false, reason: 'no-dir' };
  if (!(await ensurePermission(handle, opts.interactive ?? false))) return { ok: false, reason: 'denied' };
  try {
    const finalName = await uniqueName(handle, name);
    const fileHandle = await handle.getFileHandle(finalName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return { ok: true, name: finalName };
  } catch (e) {
    console.warn('[fileTargets] save fehlgeschlagen', e);
    return { ok: false, reason: 'error' };
  }
}

/**
 * „Speichern unter …" — öffnet den Datei-Dialog, der Nutzer wählt den Ort frei.
 * Startet wenn möglich im Typ-Ordner. Fällt auf den klassischen Download zurück, wenn der
 * Save-Picker nicht unterstützt wird.
 */
export async function saveWithDialog(name: string, blob: Blob, kind?: TargetKind): Promise<'saved' | 'cancelled' | 'unsupported'> {
  if (!savePickerSupported()) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return 'unsupported';
  }
  try {
    const startIn = kind ? await idbGet<any>(KEY(kind)) : null;
    const pickerOpts: any = { suggestedName: name };
    if (startIn) pickerOpts.startIn = startIn;
    const fileHandle = await (window as any).showSaveFilePicker(pickerOpts);
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return 'saved';
  } catch (e: any) {
    if (e?.name === 'AbortError') return 'cancelled';
    console.warn('[fileTargets] saveWithDialog fehlgeschlagen', e);
    // Letzter Fallback: klassischer Download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return 'cancelled';
  }
}

/* ───────────────────────────────────────────────────────────────────────────
 * Welle 12 (pre116): Eingangsordner „zur Unterschrift" (umgekehrte Richtung).
 * Ein gemerkter Quell-Ordner (Netzlaufwerk) wird gepollt; neue PDFs werden dem
 * Berater gemeldet. Nach Freigabe zur Unterschrift wird die Quell-PDF in den
 * Unterordner „erledigt" verschoben (Copy+Delete; die FS-Access-API kennt kein
 * atomares Move). Rein browserbasiert — funktioniert im normalen Client UND im
 * Companion (kein nativer Dienst nötig).
 * ───────────────────────────────────────────────────────────────────────────*/

const INBOX_KEY = 'dir:inbox';
const DONE_SUBDIR = 'erledigt';

export interface InboxPdf { name: string; size: number; lastModified: number; key: string }

export async function pickInboxDir(): Promise<string | null> {
  if (!dirPickerSupported()) return null;
  try {
    const handle = await (window as any).showDirectoryPicker({ mode: 'readwrite', id: 'bamoa-inbox' });
    await idbPut(INBOX_KEY, handle);
    return handle.name ?? 'Ordner';
  } catch (e: any) {
    if (e?.name === 'AbortError') return null;
    console.warn('[inbox] pick fehlgeschlagen', e);
    return null;
  }
}
export async function getInboxDirName(): Promise<string | null> {
  try { const h = await idbGet<any>(INBOX_KEY); return h?.name ?? null; } catch { return null; }
}
export async function clearInboxDir(): Promise<void> { try { await idbDel(INBOX_KEY); } catch { /* */ } }
export async function getInboxHandle(): Promise<any | null> { return idbGet<any>(INBOX_KEY); }
export async function getInboxAccess(handle?: any): Promise<AccessState> {
  const h = handle ?? await idbGet<any>(INBOX_KEY);
  return h ? await queryAccess(h) : 'none';
}
/** MUSS aus einem Klick-Handler mit bereits geladenem Handle aufgerufen werden. */
export function requestInboxAccess(handle: any): Promise<boolean> {
  if (!handle) return Promise.resolve(false);
  return handle.requestPermission({ mode: 'readwrite' }).then((s: string) => s === 'granted').catch(() => false);
}

/** PDFs im Eingangsordner auflisten (ohne den Unterordner „erledigt"). */
export async function listInboxPdfs(): Promise<InboxPdf[]> {
  const dir = await idbGet<any>(INBOX_KEY);
  if (!dir) return [];
  if (!(await ensurePermission(dir, false))) return [];
  const out: InboxPdf[] = [];
  try {
    for await (const [name, handle] of (dir as any).entries()) {
      if (handle.kind !== 'file') continue;
      if (!name.toLowerCase().endsWith('.pdf')) continue;
      try {
        const f = await handle.getFile();
        out.push({ name, size: f.size, lastModified: f.lastModified, key: `${name}|${f.size}|${f.lastModified}` });
      } catch { /* Datei evtl. gerade in Bewegung */ }
    }
  } catch (e) { console.warn('[inbox] list fehlgeschlagen', e); }
  out.sort((a, b) => a.lastModified - b.lastModified);
  return out;
}

/** Eine PDF aus dem Eingangsordner als File lesen (für Ansehen / Signatur). */
export async function readInboxPdf(name: string): Promise<File | null> {
  const dir = await idbGet<any>(INBOX_KEY);
  if (!dir) return null;
  try { const h = await dir.getFileHandle(name, { create: false }); return await h.getFile(); }
  catch { return null; }
}

/** Quell-PDF nach „erledigt" verschieben (Copy + Delete). */
export async function moveInboxToDone(name: string): Promise<boolean> {
  const dir = await idbGet<any>(INBOX_KEY);
  if (!dir) return false;
  if (!(await ensurePermission(dir, false))) return false;
  try {
    const srcH = await dir.getFileHandle(name, { create: false });
    const file = await srcH.getFile();
    const doneDir = await dir.getDirectoryHandle(DONE_SUBDIR, { create: true });
    const finalName = await uniqueName(doneDir, name);
    const dstH = await doneDir.getFileHandle(finalName, { create: true });
    const w = await dstH.createWritable();
    await w.write(file);
    await w.close();
    await dir.removeEntry(name);
    return true;
  } catch (e) { console.warn('[inbox] move→erledigt fehlgeschlagen', e); return false; }
}
